import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, faCircleNotch, faCheckCircle, 
  faBuilding, faChevronDown, faChevronUp, faTruck, faDolly
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_BASE } from '../apiConfig';
import { useCalculatorData } from './useCalculatorData';

const goldColor = '#c5a059';

const UnifiedLogisticsERP = ({ onResultChange, order }) => {
  const { t } = useTranslation();
  const [activeNavbarId, setActiveNavbarId] = useState(null); 
  const [step, setStep] = useState(0);
  const [openGroup, setOpenGroup] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [calcLoading, setCalcLoading] = useState(false);
  const [resultData, setResultData] = useState(null);

  const [address, setAddress] = useState(order?.address || '');
  const [floor, setFloor] = useState(order?.floor || '0');
  const [elevator, setElevator] = useState('none');

  const { meta, loading } = useCalculatorData(1);

  useEffect(() => {
    if (meta.navbars && meta.navbars.length > 0 && !activeNavbarId) {
      setActiveNavbarId(meta.navbars[0].id);
    }
  }, [meta.navbars, activeNavbarId]);

  useEffect(() => {
    setOpenGroup(null);
  }, [activeNavbarId]);

  const handleUpdate = (id, val) => {
    if (val === '') {
      setSelectedItems(prev => ({ ...prev, [id]: '' }));
      return;
    }
    const num = Math.max(0, parseInt(val) || 0);
    setSelectedItems(prev => ({ ...prev, [id]: num }));
  };

  const currentStructure = useMemo(() => {
    const { navbars, groups, assets } = meta;
    if (!navbars || !groups || !assets || !activeNavbarId) return [];
    const selectedNav = navbars.find(n => n.id === activeNavbarId);
    if (!selectedNav) return [];

    const childGroups = groups
      .filter(g => Number(g.navbar_id) === Number(activeNavbarId))
      .map(g => ({
        ...g,
        items: assets.filter(a => Number(a.group_id) === Number(g.id))
      }))
      .filter(g => g.items.length > 0);

    return [{ ...selectedNav, childGroups }];
  }, [meta, activeNavbarId]);

  const calculate = async () => {
    const itemsToProcess = Object.entries(selectedItems)
        .filter(([_, qty]) => qty > 0 && qty !== '')
        .map(([id, qty]) => {
            const found = meta.assets.find(a => String(a.id) === String(id));
            return { asset_id: id, name: found?.name || "Item", quantity: Number(qty) };
        });

    if (itemsToProcess.length === 0) return alert(t('select_item_alert'));

    setCalcLoading(true);
    try {
        const res = await axios.post(`${API_BASE}/api/calculators/calculate`, {
            calculatorId: activeNavbarId,
            address, 
            floor: parseInt(floor), 
            elevator, 
            items: itemsToProcess
        });

        const { totalPrice, summary, business } = res.data;
        
        // Dynamic translation using flat keys from your JSON
        const elText = t(`elevator_${elevator}`);
        const itemsListStr = itemsToProcess.map(i => `• ${i.name}: ${i.quantity}`).join('\n');
        
        const combinedMessage = `
${t('address')}: ${address || t('not_specified')}
${t('floor')}: ${floor}
${t('elevator')}: ${elText}
${t('items_list')}:
${itemsListStr}
-------------------------
${t('summary')}: ${summary}
`.trim();

        setResultData({ 
            price: totalPrice, 
            summary, 
            fullDetailsText: combinedMessage,
            itemsList: itemsToProcess,
            details: { address, floor, elevator: elText }
        });

        if (onResultChange) {
            onResultChange(totalPrice, summary, {
                totalPrice,
                total_price: totalPrice,
                truck_price: business?.truckCost || 0,
                details: summary,
                message: combinedMessage,
                requested_vehicles: business?.vehicleSlots || [], 
                business, 
                category_name: 'LOGISTICS', 
                location: address,
                truck_quantity: business?.truckQuantity || 0
            });
        }
        setStep(1);
    } catch (e) {
        alert(t('error'));
    } finally {
        setCalcLoading(false);
    }
  };

  return (
    <div style={s.outerWrapper}>
      <div style={s.container}>
        <div style={s.header}>
          <h2 style={s.logoText}>LIBER <span style={{ color: goldColor }}>LOGISTICS</span></h2>
        </div>
        
        <div style={s.body}>
          {step === 0 ? (
            <div style={s.flexBody}>
              {/* --- Location Section --- */}
              <div style={s.sectionCard}>
                <div style={s.locationRow}>
                  <div style={{ flex: 2 }}>
                    <label style={s.label}>{t('address_label')}</label>
                    <div style={s.inputWrapper}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={s.innerIcon} />
                      <input 
                        placeholder={t('address_placeholder')} 
                        value={address} 
                        onChange={e => setAddress(e.target.value)} 
                        style={s.minimalIn} 
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>{t('floor_label')}</label>
                    <div style={s.inputWrapper}>
                      <FontAwesomeIcon icon={faBuilding} style={s.innerIcon} />
                      <input 
                        type="number" 
                        value={floor} 
                        onChange={e => setFloor(e.target.value)} 
                        style={s.minimalIn} 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Elevator Select - Label removed as requested */}
                <div style={{marginTop: '10px'}}>
                  <select value={elevator} onChange={e => setElevator(e.target.value)} style={s.minimalSelect}>
                    <option value="none">{t('elevator_none')}</option>
                    <option value="small">{t('elevator_small')}</option>
                    <option value="large">{t('elevator_large')}</option>
                  </select>
                </div>
              </div>

              {/* --- Navbar --- */}
              <div style={s.navbarScrollWrapper}>
                <div style={s.navbar}>
                  {meta.navbars?.map((nav) => (
                    <div key={nav.id} onClick={() => setActiveNavbarId(nav.id)}
                      style={{ 
                        ...s.navItem, 
                        color: activeNavbarId === nav.id ? goldColor : '#94a3b8', 
                        borderBottom: activeNavbarId === nav.id ? `3px solid ${goldColor}` : 'none' 
                      }}>
                      {nav.name}
                    </div>
                  ))}
                </div>
              </div>

              {/* --- Items List --- */}
              <div style={s.itemsContainer}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '50px' }}>
                    <FontAwesomeIcon icon={faCircleNotch} spin size="2x" color={goldColor} />
                  </div>
                ) : (
                  currentStructure.map(nav => (
                    <div key={nav.id}>
                      {nav.childGroups.map(group => (
                        <div key={group.id} style={s.goldGroupCard}>
                          <div style={s.categoryHeader} onClick={() => setOpenGroup(openGroup === group.id ? null : group.id)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span>{group.icon || '📁'}</span>
                              <span style={s.catName}>{group.group_name || group.name}</span>
                            </div>
                            <FontAwesomeIcon icon={openGroup === group.id ? faChevronUp : faChevronDown} size="xs" color={goldColor} />
                          </div>
                          {openGroup === group.id && (
                            <div style={s.universalGrid}>
                              {group.items.map(item => (
                                <div key={item.id} style={{ 
                                  ...s.itemCard, 
                                  border: selectedItems[item.id] > 0 ? `1px solid ${goldColor}` : '1px solid #f1f5f9' 
                                }}>
                                  <span style={{ fontSize: '20px' }}>{item.icon}</span>
                                  <div style={s.itemName}>{item.name}</div>
                                  <div style={s.cardCounter}>
                                    <button onClick={() => handleUpdate(item.id, (selectedItems[item.id] || 0) - 1)} style={s.qtyBtn}>-</button>
                                    <input 
                                      type="number" 
                                      value={selectedItems[item.id] ?? 0} 
                                      onChange={(e) => handleUpdate(item.id, e.target.value)} 
                                      style={s.qtyInput} 
                                    />
                                    <button onClick={() => handleUpdate(item.id, (selectedItems[item.id] || 0) + 1)} style={s.qtyBtn}>+</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
              <button style={s.mainBtn} onClick={calculate} disabled={calcLoading}>
                {calcLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('calculate_btn')}
              </button>
            </div>
          ) : (
            /* --- Success View --- */
            <div style={s.successCard}>
              <div style={s.successHeader}>
                 <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#10b981" />
                 <h2 style={{ margin: '10px 0', fontSize: '24px' }}>{resultData?.price?.toLocaleString()} ֏</h2>
              </div>

              <div style={s.fullInfoBox}>
                <div style={s.infoRow}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} width={15}/> 
                  <span>{resultData?.details.address || t('not_specified')}</span>
                </div>
                <div style={s.infoRow}>
                  <FontAwesomeIcon icon={faBuilding} width={15}/> 
                  <span>{t('floor')}: {resultData?.details.floor} ({resultData?.details.elevator})</span>
                </div>
                
                <div style={s.divider} />
                
                <div style={s.itemsReviewTitle}><FontAwesomeIcon icon={faDolly} /> {t('selected_items')}</div>
                <div style={s.reviewItemsList}>
                  {resultData?.itemsList.map((it, idx) => (
                    <div key={idx} style={s.reviewItem}>• {it.name} <span style={{color: goldColor}}>x{it.quantity}</span></div>
                  ))}
                </div>

                <div style={s.divider} />
                
                <div style={s.summaryNote}>
                   <FontAwesomeIcon icon={faTruck} style={{marginRight: '8px'}} />
                   {resultData?.summary}
                </div>
              </div>

              <button style={{ ...s.mainBtn, marginTop: '20px' }} onClick={() => setStep(0)}>
                {t('new_calc_btn')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const s = {
  outerWrapper: { 
    display: 'flex', 
    justifyContent: 'center', 
    background: '#f1f5f9', 
    width: '100%',
    height: '100dvh', // Օգտագործում ենք dynamic viewport բարձրություն
    fontFamily: 'inherit',
    overflow: 'hidden', // Չենք թողնում, որ wrapper-ը սքրոլ լինի
    boxSizing: 'border-box'
  },
  container: { 
    width: '100%', 
    maxWidth: window.innerWidth <= 480 ? '100%' : '450px', 
    height: '100%', // Լրիվ բարձրություն
    display: 'flex', 
    flexDirection: 'column', 
    background: '#fff', 
    // Եթե հեռախոս է՝ կլորացում չկա, եթե դեսքթոփ է՝ կա
    borderRadius: window.innerWidth <= 480 ? '0' : '24px', 
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)', 
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  header: { 
    background: '#0f172a', 
    padding: '15px', 
    textAlign: 'center',
    flexShrink: 0 // Չի թողնում, որ հեդերը սեղմվի
  },
  logoText: { color: '#fff', margin: 0, fontSize: '14px', fontWeight: '900', letterSpacing: '2px' },
  body: { 
    padding: '15px', 
    flex: 1, 
    display: 'flex', 
    flexDirection: 'column', 
    overflow: 'hidden', // Հիմնական բոդին չի սքրոլվում
    boxSizing: 'border-box'
  },
  flexBody: { 
    display: 'flex', 
    flexDirection: 'column', 
    height: '100%', 
    overflow: 'hidden' 
  },
  sectionCard: { 
    background: '#f8fafc', 
    border: '1px solid #e2e8f0', 
    borderRadius: '16px', 
    padding: '12px', 
    marginBottom: '12px',
    flexShrink: 0 // Լոկացիայի մասը միշտ երևում է
  },
  locationRow: { display: 'flex', gap: '8px' },
  label: { fontSize: '10px', fontWeight: '800', color: '#64748b', marginBottom: '4px', display: 'block', textTransform: 'uppercase' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  innerIcon: { position: 'absolute', left: '10px', color: goldColor, fontSize: '12px' },
  minimalIn: { width: '100%', padding: '10px 10px 10px 30px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px', boxSizing: 'border-box' },
  minimalSelect: { width: '100%', padding: '8px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', outline: 'none' },
  navbarScrollWrapper: { 
    overflowX: 'auto', 
    marginBottom: '10px',
    flexShrink: 0,
    scrollbarWidth: 'none', // Firefox-ի համար
    msOverflowStyle: 'none'  // IE-ի համար
  },
  navbar: { 
    display: 'flex', 
    borderBottom: '1px solid #e2e8f0',
    minWidth: 'max-content' // Թույլ է տալիս, որ սքրոլ լինի, ոչ թե սեղմվի
  }, 
  navItem: { 
    padding: '12px 20px', 
    fontSize: '10px', 
    fontWeight: '800', 
    cursor: 'pointer', 
    textTransform: 'uppercase', 
    whiteSpace: 'nowrap',
    flexShrink: 0 
  },
  itemsContainer: { 
    flex: 1, 
    overflowY: 'auto', // Միայն ապրանքների ցուցակն է սքրոլ լինում
    paddingRight: '4px',
    WebkitOverflowScrolling: 'touch' // iOS-ի սահուն սքրոլի համար
  },
  goldGroupCard: { marginBottom: '8px', background: '#fffcf5', borderRadius: '14px', border: `1px solid ${goldColor}33` },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', padding: '12px 15px', cursor: 'pointer', alignItems: 'center' },
  catName: { fontWeight: '800', fontSize: '11px', color: '#1e293b' },
  universalGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', padding: '10px', background: '#fff', borderRadius: '0 0 14px 14px' },
  itemCard: { borderRadius: '12px', padding: '10px 5px', textAlign: 'center', minHeight: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #f1f5f9' },
  itemName: { fontSize: '9px', fontWeight: '700', color: '#475569', marginTop: '5px' },
  cardCounter: { display: 'flex', alignItems: 'center', background: '#f1f5f9', borderRadius: '8px', marginTop: '8px' },
  qtyBtn: { border: 'none', background: 'none', color: goldColor, fontWeight: 'bold', padding: '4px 8px', cursor: 'pointer' },
  qtyInput: { width: '30px', border: 'none', background: 'transparent', textAlign: 'center', fontSize: '11px', fontWeight: '800' },
  mainBtn: { 
    width: '100%', 
    padding: '16px', 
    borderRadius: '14px', 
    border: 'none', 
    background: '#0f172a', 
    color: '#fff', 
    fontWeight: '900', 
    cursor: 'pointer', 
    letterSpacing: '1px',
    flexShrink: 0, // Կոճակը միշտ պետք է երևա ներքևում
    marginTop: '10px'
  },
  successCard: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  successHeader: { textAlign: 'center', marginBottom: '15px', flexShrink: 0 },
  fullInfoBox: { flex: 1, background: '#f8fafc', borderRadius: '20px', border: '1px solid #e2e8f0', padding: '20px', overflowY: 'auto' },
  infoRow: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#334155', marginBottom: '8px' },
  divider: { height: '1px', background: '#e2e8f0', margin: '15px 0' },
  itemsReviewTitle: { fontSize: '12px', fontWeight: '800', color: goldColor, marginBottom: '10px', textTransform: 'uppercase' },
  reviewItemsList: { display: 'flex', flexDirection: 'column', gap: '5px' },
  reviewItem: { fontSize: '12px', color: '#475569' },
  summaryNote: { fontSize: '13px', fontWeight: '700', color: '#0f172a', lineHeight: '1.4' }
};
export default UnifiedLogisticsERP;